import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Card, CardBody, CardFooter } from 'reactstrap';
import { UserNoteBan, UserNoteSummary } from '../../../shared/user/notes';
import { ActionButtonModal, DateTime, MaterialIcon, PermissionBoundary } from '../../components';
import { useTranslation } from '../../translations';
import { capitalize, renderName } from '../../utils';
import { noteIcons } from './addNoteForm';

interface UserNoteProps {
  readonly note: UserNoteSummary;
  readonly refresh: () => void;
}

export const UserNoteItem: FC<UserNoteProps> = ({ note, refresh }) => {
  const { color, icon } = noteIcons[note.category];
  const { ts } = useTranslation();
  return (
    <Card className="mb-10" id={`note${note.id}`}>
      <CardBody>
        <span style={{ float: 'left', paddingRight: '10px' }}>
          <MaterialIcon medium name={icon} type={color} />
        </span>
        <PermissionBoundary inline requiredPermissions="user:note:delete">
          <span style={{ float: 'right', paddingLeft: '10px' }}>
            <ActionButtonModal
              actionContent="common.delete"
              buttonContent={<MaterialIcon className="delete" name="delete" />}
              color="danger"
              id="deleteNote"
              onComplete={async () => {
                await api.deleteUserNote(note.id);
                refresh();
              }}
              title="delete_note"
            >
              {ts('are_you_sure_you_want_to_delete_this_note')}
            </ActionButtonModal>
          </span>
        </PermissionBoundary>
        <div>
          <NoteTitle note={note} />
          {note.ban && <BanPill ban={note.ban} />}
        </div>
        {note.text}
      </CardBody>
      <CardFooter>
        {capitalize(note.category)} {ts('note.created_by')}{' '}
        <Link to={`/housekeeping/attendees/user/${note.author.id}`}>{renderName(note.author)}</Link>{' '}
        {ts('note.on')} <DateTime value={note.createdAt} />
      </CardFooter>
    </Card>
  );
};

const NoteTitle: FC<{ readonly note: UserNoteSummary }> = ({ note }) => {
  const { ts } = useTranslation();
  if (note.user) {
    return <Link to={`/housekeeping/attendees/user/${note.user.id}`}>{renderName(note.user)}</Link>;
  }

  if (note.ban) {
    return (
      <span>
        {note.ban.firstName} {note.ban.lastName}
      </span>
    );
  }

  return <span>{ts('unknown_user')}</span>;
};

const BanPill: FC<{ readonly ban: UserNoteBan }> = ({ ban }) => {
  const { ts } = useTranslation();
  return (
    <Badge color="danger" pill>
      {ts('note.ban')} {ban.category === 'registration' ? '' : ` on ${capitalize(ban.category)}`}
    </Badge>
  );
};
